/**
 * CollectionPanel – Manage collections (list, create, rename, delete)
 *
 * Admin-only overlay panel.
 */

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchCollections,
  createCollection,
  deleteCollection,
  updateCollection,
  type CollectionMeta,
} from "../lib/api.js";

interface Props {
  onClose: () => void;
  onSelectCollection: (id: string | null) => void;
}

export function CollectionPanel({ onClose, onSelectCollection }: Props) {
  const [collections, setCollections] = useState<CollectionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const cols = await fetchCollections();
      setCollections(cols);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi");
    }
    setLoading(false);
  }, []);

  useEffect(() => { void loadCollections(); }, [loadCollections]);

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) return;
    setError(null);
    try {
      await createCollection(newName.trim());
      setNewName("");
      void loadCollections();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi");
    }
  }, [newName, loadCollections]);

  const handleDelete = useCallback(async (id: string) => {
    setError(null);
    try {
      await deleteCollection(id);
      void loadCollections();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi");
    }
  }, [loadCollections]);

  const handleRename = useCallback(async (id: string) => {
    if (!editName.trim()) return;
    setError(null);
    try {
      await updateCollection(id, { name: editName.trim() });
      setEditingId(null);
      void loadCollections();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi");
    }
  }, [editName, loadCollections]);

  const handleView = useCallback((id: string) => {
    onSelectCollection(id);
    onClose();
  }, [onSelectCollection, onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-[#1A1917] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Collections</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="bg-red-500/20 text-red-300 px-4 py-2 rounded-lg text-sm">{error}</div>
          )}

          {/* Create new */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Tên collection mới..."
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B76E6E]/50"
              onKeyDown={(e) => { if (e.key === "Enter") void handleCreate(); }}
            />
            <button
              onClick={() => { void handleCreate(); }}
              disabled={!newName.trim()}
              className="bg-[#B76E6E] hover:bg-[#a05e5e] disabled:opacity-30 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
            >
              Tạo
            </button>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#B76E6E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : collections.length === 0 ? (
            <div className="text-center text-white/30 text-sm py-8">
              Chưa có collection nào
            </div>
          ) : (
            <div className="space-y-2">
              {collections.map((col) => (
                <div
                  key={col.id}
                  className="border border-white/10 rounded-xl overflow-hidden"
                >
                  {editingId === col.id ? (
                    <div className="flex gap-2 p-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void handleRename(col.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                      <button
                        onClick={() => { void handleRename(col.id); }}
                        className="text-[#B76E6E] text-sm font-medium px-3"
                      >
                        Lưu
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-white/40 text-sm px-2"
                      >
                        Huỷ
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button
                        onClick={() => handleView(col.id)}
                        className="flex-1 text-left min-w-0"
                      >
                        <span className="text-white font-medium text-sm truncate block">
                          {col.name}
                        </span>
                        <span className="text-white/40 text-xs">
                          {col.count} ảnh
                        </span>
                      </button>
                      <button
                        onClick={() => { setEditingId(col.id); setEditName(col.name); }}
                        className="text-white/30 hover:text-white/70 text-xs px-2 transition-colors"
                        title="Đổi tên"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => { void handleDelete(col.id); }}
                        className="text-white/30 hover:text-red-400 text-xs px-2 transition-colors"
                        title="Xoá"
                      >
                        🗑
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
